import type { ReactNode } from "react";
import { get as getEmojiGlyph } from "node-emoji";

import type { ConversationIndexEntry, UsersMap } from "../types/slack";
import { parseMrkdwn, type MrkdwnNode } from "./mrkdwn";

// Slack-specific shortcode aliases that node-emoji's canonical name set
// doesn't cover directly. The most common: Slack uses :thumbsup: where
// the underlying Unicode CLDR shortcode is :+1:. node-emoji has the
// CLDR name; we map. Workspace custom emoji (entries from the archive's
// emoji.json) are out of scope for Pass 1.4 — those still render as the
// literal :shortcode: text. A future pass can wire the workspace map in.
const SLACK_EMOJI_ALIASES: Record<string, string> = {
  thumbsup: "+1",
  thumbsdown: "-1",
};

const SHORTCODE_RE = /:([a-z0-9_+\-]+):/g;

function resolveEmoji(name: string): string | undefined {
  const direct = getEmojiGlyph(name);
  if (direct !== undefined) return direct;
  const alias = SLACK_EMOJI_ALIASES[name];
  if (alias) return getEmojiGlyph(alias);
  return undefined;
}

// Walks plain text and replaces :name: tokens with rendered emoji spans
// when node-emoji recognizes them. Unknown shortcodes are preserved as
// literal text — the colons stay in place for auditability.
function renderEmojiInText(text: string): ReactNode[] {
  if (!text || !text.includes(":")) return [text];
  const out: ReactNode[] = [];
  let lastIdx = 0;
  let key = 0;
  SHORTCODE_RE.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = SHORTCODE_RE.exec(text)) !== null) {
    const shortcode = m[1]!;
    const glyph = resolveEmoji(shortcode);
    if (glyph === undefined) continue;
    if (m.index > lastIdx) out.push(text.slice(lastIdx, m.index));
    out.push(
      <span key={`e-${key++}`} data-emoji-shortcode={shortcode}>
        {glyph}
      </span>,
    );
    lastIdx = m.index + m[0].length;
  }
  if (lastIdx < text.length) out.push(text.slice(lastIdx));
  return out;
}

// HTML entity decoder for the small set the v5.1 archives are known
// to contain. Slack stores `&amp;`, `&lt;`, `&gt;`, `&quot;`,
// `&apos;` (or `&#39;` / `&#x27;`) literally in `text` because the
// underlying API double-encodes when copy-pasted from web exports.
// We MUST decode before parsing escape syntax — otherwise
// `&lt;@U123&gt;` would render as the four literal characters
// `<@U123>` rather than a resolved mention.
const HTML_ENTITIES: Record<string, string> = {
  amp: "&",
  lt: "<",
  gt: ">",
  quot: '"',
  apos: "'",
  "#39": "'",
  "#x27": "'",
};

export function decodeHtmlEntities(s: string): string {
  return s.replace(
    /&(amp|lt|gt|quot|apos|#39|#x27);/g,
    (_, name: string) => HTML_ENTITIES[name] ?? `&${name};`,
  );
}

// Pass 1.1 — minimal Slack escape resolution. Walks plain Slack message
// text and turns the four <...> escape forms into rendered React nodes.
//
// In scope: <@U…>, <#C…|name>, <url|text> / <url>, <!here|channel|everyone>.
// Out of scope: real mrkdwn (*bold* / _italic_ / lists / blockquotes / etc.).
// Triple-backtick code blocks are split out earlier in Message.tsx so this
// function only ever sees the plain (non-code) segments.

const ESCAPE_RE =
  // 1. user mention: capture user_id
  // 2. channel id, 3. optional |name
  // 4. url (http or https), 5. optional |text
  // 6. broadcast: here|channel|everyone
  /<(?:@(U[A-Z0-9]+)|#([CG][A-Z0-9]+)(?:\|([^>]+))?|(https?:\/\/[^|>\s]+)(?:\|([^>]+))?|!(here|channel|everyone))>/g;

function resolveDisplayName(
  userID: string,
  users: UsersMap,
): { name: string; missing: boolean } {
  const u = users[userID];
  if (!u) return { name: userID, missing: true };
  return {
    name: u.real_name || u.display_name || u.name || userID,
    missing: false,
  };
}

interface UserMentionProps {
  userID: string;
  users: UsersMap;
}

function UserMention({ userID, users }: UserMentionProps) {
  const { name, missing } = resolveDisplayName(userID, users);
  return (
    <span
      data-user-mention={userID}
      data-user-missing={missing ? "true" : "false"}
      className="inline bg-theme-chip-mention-bg text-theme-chip-mention-text font-medium text-[13px]"
      style={{
        padding: "var(--theme-chip-mention-padding)",
        borderRadius: "var(--theme-chip-mention-radius)",
      }}
    >
      @{name}
    </span>
  );
}

interface ChannelChipProps {
  channelID: string;
  name: string | undefined;
}

function ChannelChip({ channelID, name }: ChannelChipProps) {
  return (
    <span
      data-channel-ref={channelID}
      className="inline bg-theme-chip-channel-bg text-theme-chip-channel-text font-medium text-[13px]"
      style={{
        padding: "var(--theme-chip-mention-padding)",
        borderRadius: "var(--theme-chip-mention-radius)",
      }}
    >
      #{name && name.length > 0 ? name : channelID}
    </span>
  );
}

interface SlackLinkProps {
  url: string;
  text: string;
}

function SlackLink({ url, text }: SlackLinkProps) {
  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer noopener"
      data-slack-link={url}
      className="text-theme-accent-link underline decoration-theme-border-default hover:decoration-theme-accent-link underline-offset-2"
    >
      {text}
    </a>
  );
}

// Renders a parsed mrkdwn AST as React nodes. Each text leaf is a
// plain string for now; emoji resolution will replace those strings
// in the next commit. Theme tokens drive code/blockquote styling.
function renderMrkdwnNodes(nodes: MrkdwnNode[], keyPrefix: string): ReactNode[] {
  return nodes.map((node, i) => renderMrkdwnNode(node, `${keyPrefix}-${i}`));
}

function renderMrkdwnNode(node: MrkdwnNode, key: string): ReactNode {
  switch (node.kind) {
    case "text": {
      const parts = renderEmojiInText(node.text);
      // Single string → return as-is (no fragment needed; React handles
      // string children directly without key warnings).
      if (parts.length === 1 && typeof parts[0] === "string") return parts[0];
      return <span key={key}>{parts}</span>;
    }
    case "bold":
      return (
        <strong key={key} className="font-bold">
          {renderMrkdwnNodes(node.children, key)}
        </strong>
      );
    case "italic":
      return <em key={key}>{renderMrkdwnNodes(node.children, key)}</em>;
    case "strike":
      return <del key={key}>{renderMrkdwnNodes(node.children, key)}</del>;
    case "inline_code":
      return (
        <code
          key={key}
          className="px-1 py-0.5 rounded-sm bg-theme-mrkdwn-code-bg text-theme-mrkdwn-code-text border border-theme-mrkdwn-code-border font-mono text-[12.5px]"
        >
          {node.text}
        </code>
      );
    case "code_block":
      return (
        <pre
          key={key}
          className="my-2 px-3 py-2 rounded-sm bg-theme-mrkdwn-code-bg text-theme-mrkdwn-code-text border border-theme-mrkdwn-code-border font-mono text-xs overflow-x-auto whitespace-pre-wrap"
        >
          {node.text}
        </pre>
      );
    case "blockquote":
      return (
        <blockquote
          key={key}
          className="my-1 pl-3 border-l-4 border-theme-mrkdwn-blockquote-border text-theme-mrkdwn-blockquote-text"
        >
          {renderMrkdwnNodes(node.children, key)}
        </blockquote>
      );
  }
}

type BroadcastKind = "here" | "channel" | "everyone";

function BroadcastMention({ kind }: { kind: BroadcastKind }) {
  return (
    <span
      data-broadcast={kind}
      className="inline bg-theme-chip-broadcast-bg text-theme-chip-broadcast-text font-medium text-[13px]"
      style={{
        padding: "var(--theme-chip-mention-padding)",
        borderRadius: "var(--theme-chip-mention-radius)",
      }}
    >
      @{kind}
    </span>
  );
}

// Walks the input text, finds every <…> escape that matches one of the
// four supported forms, and returns an array of mixed strings + React
// elements. Plain text segments outside escapes are passed through
// verbatim — newlines included; the caller's whitespace-pre-wrap wrap
// preserves them in layout.
//
// Anything inside <…> that doesn't match one of the four supported
// forms (or any stray < / > outside an escape) is left as literal
// text. So `1 < 2 > 0` and `<unknown>` both render as themselves.
export function renderSlackText(
  text: string,
  users: UsersMap,
  // Optional. When provided, bare <#Cxxx> references with no |name
  // segment resolve against this list to render as the channel's
  // human-readable label. Pass workspace.conversations from the caller.
  conversations?: ConversationIndexEntry[],
): ReactNode[] {
  if (!text) return [];

  // Decode HTML entities BEFORE escape parsing so that
  // `&lt;@U123&gt;` resolves as a real mention.
  const decoded = decodeHtmlEntities(text);

  const out: ReactNode[] = [];
  let lastIndex = 0;
  let key = 0;

  // Manual loop instead of matchAll so we get the index reliably even
  // on engines that don't expose RegExpMatchArray.index for global regexes.
  ESCAPE_RE.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = ESCAPE_RE.exec(decoded))) {
    const matchStart = match.index;
    const matchEnd = matchStart + match[0].length;
    if (matchStart > lastIndex) {
      const segment = decoded.slice(lastIndex, matchStart);
      out.push(
        ...renderMrkdwnNodes(parseMrkdwn(segment), `m-${key++}`),
      );
    }

    const userID = match[1];
    const channelID = match[2];
    const channelName = match[3];
    const url = match[4];
    const linkText = match[5];
    const broadcast = match[6];

    if (userID) {
      out.push(<UserMention key={`s-${key++}`} userID={userID} users={users} />);
    } else if (channelID) {
      let resolvedName = channelName;
      if (!resolvedName && conversations) {
        const match = conversations.find((c) => c.id === channelID);
        if (match) resolvedName = match.label;
      }
      out.push(
        <ChannelChip
          key={`s-${key++}`}
          channelID={channelID}
          name={resolvedName}
        />,
      );
    } else if (url) {
      out.push(
        <SlackLink key={`s-${key++}`} url={url} text={linkText ?? url} />,
      );
    } else if (broadcast) {
      out.push(
        <BroadcastMention
          key={`s-${key++}`}
          kind={broadcast as BroadcastKind}
        />,
      );
    }

    lastIndex = matchEnd;
  }

  if (lastIndex < decoded.length) {
    const segment = decoded.slice(lastIndex);
    out.push(
      ...renderMrkdwnNodes(parseMrkdwn(segment), `m-${key++}`),
    );
  }

  return out;
}
